import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { createOrUpdateTelegramUser } from '../services/api';
import { initializeTelegramApp } from '../services/telegram';

interface UserContextType {
  user: User | null;
  telegramUser: any | null;
  isLoading: boolean;
  error: string | null;
  refreshUser: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [telegramUser, setTelegramUser] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadUserData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Initialize Telegram Web App and get user data
      const tgUser = await initializeTelegramApp();
      setTelegramUser(tgUser);
      
      if (tgUser) {
        // Create or update user in our system
        const userData = await createOrUpdateTelegramUser({
          telegramId: tgUser.id.toString(),
          telegramUsername: tgUser.username || undefined,
        });
        
        setUser(userData);
      } else {
        setError('Telegram user data not available');
      }
    } catch (err) {
      console.error('Error loading user data:', err);
      setError('Failed to load user data');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUser = async () => {
    await loadUserData();
  };

  useEffect(() => {
    loadUserData();
  }, []);

  const value = {
    user,
    telegramUser,
    isLoading,
    error,
    refreshUser
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
